import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer } from "recharts";
import { Slider } from "@/components/ui/slider";
import {
  BrainCircuit, ShieldCheck, Sparkles, Gauge, Eraser, Bell, DollarSign,
  RefreshCw, Lock, AlertTriangle, Search, PenLine, Play, ChevronRight,
} from "lucide-react";
import api, { timeAgo, apiError } from "@/lib/api";
import { PageHeader } from "@/admin/ui";

const HEALTH_TONES: Record<string, string> = {
  HEALTHY: "text-green-400 border-green-500/30 bg-[#0d1a12]",
  PAUSED: "text-amber-400 border-amber-500/30 bg-[#1a140d]",
  DEGRADED: "text-red-400 border-red-500/40 bg-[#1a0d0d]",
  BACKLOGGED: "text-[#A78BFA] border-[#7C3AED]/40 bg-[#120d1a]",
  ERROR: "text-red-400 border-red-500/40 bg-[#1a0d0d]",
};

const STATE_BADGE: Record<string, string> = {
  COMPLETE: "text-green-400 border-green-500/30 bg-green-500/[0.06]",
  PARTIAL: "text-amber-400 border-amber-500/30 bg-amber-500/[0.06]",
  PENDING: "text-[#00E5FF] border-[#00E5FF]/30 bg-[#00E5FF]/[0.05]",
  FAILED: "text-red-400 border-red-500/40 bg-red-500/[0.06]",
  EDITOR_LOCKED: "text-[#A78BFA] border-[#7C3AED]/40 bg-[#7C3AED]/[0.08]",
  NOT_ELIGIBLE: "text-gray-500 border-white/10 bg-white/[0.03]",
  PAUSED: "text-amber-400 border-amber-500/30 bg-amber-500/[0.06]",
  LOW_CONFIDENCE: "text-amber-400 border-amber-500/30 bg-amber-500/[0.06]",
};

const FILTERS = [
  { key: "", label: "All" }, { key: "COMPLETE", label: "Complete" },
  { key: "PARTIAL", label: "Partial" }, { key: "PENDING", label: "Pending" },
  { key: "FAILED", label: "Failed" }, { key: "EDITOR_LOCKED", label: "Locked" },
  { key: "NOT_ELIGIBLE", label: "Not eligible" },
];

const StateBadge = ({ state }: { state: string }) => (
  <span data-testid={`intel-state-${state}`}
    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider whitespace-nowrap ${STATE_BADGE[state] || STATE_BADGE.NOT_ELIGIBLE}`}>
    {state === "EDITOR_LOCKED" && <Lock className="h-2.5 w-2.5" />}
    {state.replace(/_/g, " ")}
  </span>
);

const Stat = ({ icon: Icon, label, value, sub, tone = "#00E5FF", testid, onClick }: any) => (
  <div className={`bg-[#121212] border border-white/10 rounded-xl p-4 ${onClick ? "cursor-pointer hover:border-[#7C3AED]/50 hover:bg-[#16131f] transition-colors group" : ""}`}
    data-testid={testid} onClick={onClick} role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e: any) => e.key === "Enter" && onClick() : undefined}>
    <p className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-gray-500">
      <span className="flex items-center gap-2"><Icon className="h-3.5 w-3.5" style={{ color: tone }} /> {label}</span>
      {onClick && <ChevronRight className="h-3 w-3 text-gray-600 group-hover:text-[#A78BFA] transition-colors" />}
    </p>
    <p className="font-mono text-2xl font-bold text-white mt-2">{value}</p>
    {sub && <p className="font-mono text-[11px] text-gray-500 mt-1">{sub}</p>}
  </div>
);

const Chip = ({ label, value, tone = "text-gray-300" }: any) => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 font-mono text-[10px] text-gray-500">
    {label} <span className={`font-bold ${tone}`}>{value}</span>
  </span>
);

const ModuleCoverage = ({ label, c, testid }: any) => {
  const pct = c?.eligible ? Math.round((c.generated / c.eligible) * 100) : 0;
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between font-mono text-[11px] mb-1.5">
        <span className="uppercase tracking-widest text-gray-500">{label}</span>
        <span className="text-white font-bold">{pct}% <span className="text-gray-600 font-normal">({c?.generated ?? 0}/{c?.eligible ?? 0} eligible)</span></span>
      </div>
      <div className="h-[6px] rounded-full bg-white/[0.06] overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#00E5FF]" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        <Chip label="pending" value={c?.pending ?? 0} tone="text-[#00E5FF]" />
        <Chip label="failed" value={c?.failed ?? 0} tone={c?.failed ? "text-red-400" : "text-gray-300"} />
        <Chip label="none found" value={c?.none_found ?? 0} />
        <Chip label="suppressed" value={c?.suppressed ?? 0} />
        <Chip label="locked" value={c?.locked ?? 0} tone="text-[#A78BFA]" />
        <Chip label="not eligible" value={c?.not_eligible ?? 0} />
      </div>
    </div>
  );
};

const Panel = ({ title, icon: Icon, sub, children, testid, className = "" }: any) => (
  <div className={`bg-[#121212] border border-white/10 rounded-xl p-5 ${className}`} data-testid={testid}>
    <h2 className="flex items-center gap-2 font-heading font-bold text-white text-base mb-1">
      {Icon && <Icon className="h-4 w-4 text-[#00E5FF]" />} {title}
    </h2>
    {sub && <p className="font-mono text-[11px] text-gray-500 mb-4">{sub}</p>}
    {children}
  </div>
);

export default function Intelligence() {
  const qc = useQueryClient();
  const [win, setWin] = useState("24h");
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(25);
  const [retrying, setRetrying] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ["intel-health", win],
    queryFn: async () => (await api.get(`/admin/intelligence/health?window=${win}`)).data,
    refetchInterval: 60000,
  });
  const { data: arts } = useQuery({
    queryKey: ["intel-articles", filter, q, limit],
    queryFn: async () => (await api.get("/admin/intelligence/articles", {
      params: { state: filter || undefined, q: q || undefined, limit },
    })).data,
  });
  const { data: resume } = useQuery({
    queryKey: ["intel-resume-status"],
    queryFn: async () => (await api.get("/admin/intelligence/resume-status")).data,
    refetchInterval: (query) => (query.state.data?.running ? 4000 : 30000),
  });
  const { data: spend } = useQuery({
    queryKey: ["intel-spend"],
    queryFn: async () => (await api.get("/admin/intelligence/spend")).data,
  });
  const [capDraft, setCapDraft] = useState<number | null>(null);
  const [savingCap, setSavingCap] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  const emailReport = async () => {
    setSendingReport(true);
    try {
      const res = await api.post("/admin/intelligence/spend-report/send");
      toast.success(res.data.message);
    } catch (e) { toast.error(apiError(e)); }
    setSendingReport(false);
  };

  const saveCap = async () => {
    if (capDraft === null || capDraft === spend?.cap) return;
    setSavingCap(true);
    try {
      const res = await api.put("/admin/intelligence/spend-cap", { cap: capDraft });
      const a = res.data.action;
      toast.success(a === "resumed" ? `Cap set to $${capDraft}/day — AI resumed (spend is under the new cap)`
        : a === "paused" ? `Cap set to $${capDraft}/day — AI paused (today's spend already exceeds it)`
        : `Daily cap set to $${capDraft}/day`);
      setCapDraft(null);
      qc.invalidateQueries({ queryKey: ["intel-spend"] });
      qc.invalidateQueries({ queryKey: ["intel-health"] });
    } catch (e) { toast.error(apiError(e)); }
    setSavingCap(false);
  };

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["intel-health"] });
    qc.invalidateQueries({ queryKey: ["intel-articles"] });
    qc.invalidateQueries({ queryKey: ["intel-resume-status"] });
  };

  const retry = async (id: string, force = false) => {
    setRetrying(id);
    try {
      const res = await api.post(`/admin/intelligence/retry/${id}?force=${force}`);
      const d = res.data;
      d.error ? toast.warning(`${d.message} — ${d.error}`) : toast.success(d.message);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
    setRetrying(null);
  };

  const resumePending = async () => {
    setRetrying("__pending__");
    try {
      const res = await api.post("/admin/intelligence/retry-pending");
      res.data.started ? toast.success(res.data.message) : toast.info(res.data.message);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
    setRetrying(null);
  };

  const retryFailed = async () => {
    setRetrying("__all__");
    try {
      const res = await api.post("/admin/intelligence/retry-failed");
      toast.success(res.data.message);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
    setRetrying(null);
  };

  const svc = data?.service;
  const cov = data?.coverage;
  const san = data?.sanitizer;
  const sum = data?.summary;
  const ov = data?.overrides;
  const paused = !!svc?.paused;
  const svcBad = paused || ["UNAVAILABLE", "NOT CONFIGURED"].includes(svc?.state);
  const draining = !!resume?.running;
  const wasDraining = useRef(false);
  useEffect(() => {
    if (wasDraining.current && !draining) {
      qc.invalidateQueries({ queryKey: ["intel-health"] });
      qc.invalidateQueries({ queryKey: ["intel-articles"] });
      toast.success(resume?.stopped_reason === "provider_paused"
        ? "Resume run paused — provider limit hit again, remaining items stay queued"
        : "Resume run complete — the pending queue is drained");
    }
    wasDraining.current = draining;
  }, [draining, resume?.stopped_reason, qc]);
  const pct = draining && resume?.total > 0
    ? Math.min(100, Math.round((resume.processed / resume.total) * 100)) : 0;

  const drillTo = (state: string) => {
    setFilter(state);
    setTimeout(() => document.querySelector('[data-testid="intel-articles-panel"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  return (
    <div data-testid="admin-intelligence-page">
      <PageHeader title="Intelligence Health" sub="Extraction coverage, queue visibility, sanitizer audit and AI budget — works even while the provider is down"
        actions={
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-white/10 overflow-hidden">
              {["24h", "7d", "30d"].map((w) => (
                <button key={w} onClick={() => setWin(w)} data-testid={`intel-window-${w}`}
                  className={`px-3 py-1.5 font-mono text-[11px] uppercase transition-colors ${win === w ? "bg-[#7C3AED]/20 text-white" : "text-gray-500 hover:text-gray-300"}`}>
                  {w}
                </button>
              ))}
            </div>
            <button onClick={resumePending} disabled={draining || retrying === "__pending__"} data-testid="intel-resume-pending-button"
              className="inline-flex items-center gap-2 rounded-lg border border-[#7C3AED]/40 bg-[#7C3AED]/10 px-3 py-1.5 font-mono text-[11px] uppercase text-[#A78BFA] hover:text-white hover:border-[#7C3AED]/70 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Clears any provider-limit pause and processes all pending articles now">
              <Play className={`h-3.5 w-3.5 ${draining || retrying === "__pending__" ? "animate-pulse" : ""}`} /> {draining ? "Draining…" : "Resume pending"}
            </button>
            <button onClick={retryFailed} disabled={paused || retrying === "__all__"} data-testid="intel-retry-failed-button"
              className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/[0.04] px-3 py-1.5 font-mono text-[11px] uppercase text-gray-300 hover:text-white hover:border-[#00E5FF]/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <RefreshCw className={`h-3.5 w-3.5 ${retrying === "__all__" ? "animate-spin" : ""}`} /> Retry failed
            </button>
          </div>
        } />

      {/* Overall state banner */}
      <div className={`rounded-xl border p-4 flex flex-wrap items-center gap-3 ${HEALTH_TONES[data?.state] || "border-white/10 bg-[#121212] text-gray-400"}`}
        data-testid="intel-state-banner">
        <span className="font-mono text-lg font-bold tracking-wider">{data?.state || "…"}</span>
        <span className="font-mono text-xs text-gray-400">{data?.state_message}</span>
      </div>

      {/* Live resume drain progress */}
      {draining && (
        <div className="mt-4 rounded-xl border border-[#7C3AED]/40 bg-[#120d1a] p-4" data-testid="intel-resume-progress">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[#A78BFA]">
              <Play className="h-3.5 w-3.5 animate-pulse" /> Resume run in progress — draining the pending queue
            </p>
            <p className="font-mono text-xs text-gray-400" data-testid="intel-resume-progress-count">
              <span className="text-white font-bold">{resume.processed}</span> / {resume.total} processed
              <span className="ml-2 text-[#A78BFA]">{pct}%</span>
            </p>
          </div>
          <div className="mt-3 h-2 rounded-full bg-white/[0.06] overflow-hidden" data-testid="intel-resume-progress-bar">
            <div className="h-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] transition-[width] duration-700"
              style={{ width: `${Math.max(pct, 2)}%` }} />
          </div>
          <p className="mt-2 font-mono text-[10px] text-gray-500">
            Auto-pauses again if the provider limit is hit — updates every few seconds
            {resume.started_at ? ` · started ${timeAgo(resume.started_at)}` : ""}
          </p>
        </div>
      )}

      {/* Summary stats — click any card to drill into the matching article list */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
        <Stat icon={Sparkles} label="Processed (24h)" value={sum?.processed_today ?? "—"} sub="extraction jobs completed — click to view" testid="intel-stat-processed"
          onClick={() => drillTo("COMPLETE")} />
        <Stat icon={BrainCircuit} label="Pending queue" value={sum?.pending ?? "—"} tone="#A78BFA"
          sub={paused ? "waiting for provider resume — click to view" : "background sweep ≤10 min — click to view"} testid="intel-stat-pending"
          onClick={() => drillTo("PENDING")} />
        <Stat icon={AlertTriangle} label="Failed" value={sum?.failed_open ?? "—"} tone={sum?.failed_open ? "#F87171" : "#22C55E"}
          sub={`${sum?.failed_24h ?? 0} in last 24h — click to view`} testid="intel-stat-failed"
          onClick={() => drillTo("FAILED")} />
        <Stat icon={Lock} label="Editor locked" value={sum?.locked ?? "—"} tone="#A78BFA" sub="snapshot or WTM locked — click to view" testid="intel-stat-locked"
          onClick={() => drillTo("EDITOR_LOCKED")} />
        <Stat icon={DollarSign} label="AI spend est." value={data?.usage ? `$${data.usage.d1.total_cost}` : "—"} tone="#22C55E"
          sub={data?.usage ? `$${data.usage.d7.total_cost} last 7 days — click for breakdown` : ""} testid="intel-stat-spend"
          onClick={() => document.querySelector('[data-testid="intel-spend-panel"]')?.scrollIntoView({ behavior: "smooth", block: "center" })} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Extraction service */}
        <div className={`rounded-xl border p-5 ${svcBad ? "bg-[#1a120d] border-amber-500/30" : "bg-[#0d1a12] border-green-500/30"}`}
          data-testid="intel-llm-status">
          <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">
            <Gauge className={`h-3.5 w-3.5 ${svcBad ? "text-amber-400" : "text-green-400"}`} /> Extraction service
          </p>
          <p className={`font-mono text-2xl font-bold mt-2 ${svcBad ? "text-amber-400" : "text-green-400"}`}>
            {svc?.state || "—"}
          </p>
          <div className="mt-3 space-y-1.5 font-mono text-[11px] text-gray-500">
            {paused && <p>Auto-retry {svc?.paused_until ? timeAgo(svc.paused_until) : "soon"} — existing intelligence stays live</p>}
            {!paused && <p>Next retry: {svc?.next_retry?.includes?.("T") ? timeAgo(svc.next_retry) : svc?.next_retry}</p>}
            <p>Retry queue: <span className="text-white font-bold">{svc?.retry_queue ?? 0}</span> articles</p>
            {svc?.last_success_at && <p>Last successful extraction {timeAgo(svc.last_success_at)}</p>}
          </div>
          {svc?.last_error && (
            <p className={`font-mono text-[10px] mt-3 line-clamp-2 ${svcBad ? "text-amber-300/80" : "text-gray-600"}`} data-testid="intel-llm-error">
              Last provider error{svc.error_at ? ` (${timeAgo(svc.error_at)})` : ""}: {svc.last_error}
            </p>
          )}
        </div>

        {/* Families */}
        <Panel title="Context family mix" sub="Router output across the latest 100 published stories" testid="intel-families-panel">
          <div className="flex flex-wrap gap-2">
            {(data?.families || []).map((f: any) => (
              <span key={f.family} data-testid={`family-${f.family}`}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-3 py-1.5 font-mono text-[11px] text-gray-300">
                {f.family.replace(/_/g, " ")} <span className="text-[#00E5FF] font-bold">{f.count}</span>
              </span>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-white/5 flex items-center gap-2 font-mono text-[11px] text-gray-500">
            <Bell className="h-3.5 w-3.5 text-[#A78BFA]" />
            <span><span className="text-white font-bold">{data?.story_follows ?? 0}</span> active story follows by readers</span>
            {(data?.low_confidence || []).length > 0 && (
              <span className="ml-auto text-amber-400">{data.low_confidence.length} low-confidence routings</span>
            )}
          </div>
        </Panel>
      </div>

      {/* AI spend trend + daily cap control */}
      <Panel title="AI spend & daily budget" icon={DollarSign} className="mt-6" testid="intel-spend-panel"
        sub="Estimated spend per day over the last 30 days — the cap pauses AI until midnight UTC when reached">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 h-48" data-testid="intel-spend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={spend?.trend || []} margin={{ top: 8, right: 8, bottom: 0, left: -14 }}>
                <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 9, fontFamily: "JetBrains Mono" }}
                  tickFormatter={(d: string) => d.slice(5)} axisLine={false} tickLine={false} interval={4} />
                <YAxis tick={{ fill: "#6b7280", fontSize: 10, fontFamily: "JetBrains Mono" }}
                  tickFormatter={(v: number) => `$${v}`} axisLine={false} tickLine={false}
                  domain={[0, (dataMax: number) => Math.max(Math.ceil(dataMax), Math.ceil((spend?.cap || 0) * 1.15))]} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 8, fontSize: 11, fontFamily: "JetBrains Mono", color: "#fff" }}
                  formatter={(v: any, name: any) => [`$${Number(v).toFixed(2)}`, name]} />
                {spend?.cap > 0 && (
                  <ReferenceLine y={spend.cap} stroke="#F59E0B" strokeDasharray="4 4"
                    label={{ value: `cap $${spend.cap}`, fill: "#F59E0B", fontSize: 10, fontFamily: "JetBrains Mono", position: "insideTopRight" }} />
                )}
                <Bar dataKey="enrichment" stackId="s" name="Enrichment" fill="#7C3AED" />
                <Bar dataKey="images" stackId="s" name="Images" fill="#00E5FF" />
                <Bar dataKey="briefs" stackId="s" name="Briefs & text" fill="#22C55E" />
                <Bar dataKey="translation" stackId="s" name="Translations" fill="#EC4899" />
                <Bar dataKey="audio" stackId="s" name="Audio" fill="#F59E0B" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2" data-testid="intel-spend-legend">
              {[["Enrichment", "#7C3AED"], ["Images", "#00E5FF"], ["Briefs & text", "#22C55E"],
                ["Translations", "#EC4899"], ["Audio", "#F59E0B"]].map(([label, color]) => (
                <span key={label} className="inline-flex items-center gap-1.5 font-mono text-[10px] text-gray-500">
                  <span className="h-2 w-2 rounded-sm" style={{ background: color }} /> {label}
                </span>
              ))}
            </div>
          </div>
          <div className="flex flex-col" data-testid="intel-cap-control">
            <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Daily spend cap</p>
            <p className="font-mono text-3xl font-bold text-white mt-1" data-testid="intel-cap-value">
              ${capDraft ?? spend?.cap ?? "—"}<span className="text-sm text-gray-500 font-normal">/day</span>
            </p>
            <div className="mt-4">
              <Slider value={[capDraft ?? spend?.cap ?? 10]} min={1} max={50} step={1}
                onValueChange={(v: number[]) => setCapDraft(v[0])} data-testid="intel-cap-slider"
                className="[&_[role=slider]]:border-[#7C3AED] [&_.bg-primary]:bg-[#7C3AED]" />
              <div className="flex justify-between font-mono text-[10px] text-gray-600 mt-1.5">
                <span>$1</span><span>$50</span>
              </div>
            </div>
            <div className="mt-5">
              <div className="flex justify-between font-mono text-[11px]">
                <span className="text-gray-500">Spent today (est.)</span>
                <span className={spend?.spent_today >= spend?.cap ? "text-amber-400 font-bold" : "text-white font-bold"}
                  data-testid="intel-spent-today">${spend?.spent_today?.toFixed?.(2) ?? "—"}</span>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                <div className={`h-full rounded-full ${spend?.spent_today >= spend?.cap ? "bg-amber-400" : "bg-[#22C55E]"}`}
                  style={{ width: `${Math.min(100, spend?.cap ? (spend.spent_today / spend.cap) * 100 : 0)}%` }} />
              </div>
            </div>
            <button onClick={saveCap} disabled={savingCap || capDraft === null || capDraft === spend?.cap}
              data-testid="intel-cap-save-button"
              className="mt-auto pt-0 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-[#7C3AED]/40 bg-[#7C3AED]/10 px-3 py-2 font-mono text-[11px] uppercase text-[#A78BFA] hover:text-white hover:border-[#7C3AED]/70 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              {savingCap ? "Saving…" : "Save cap"}
            </button>
            <button onClick={emailReport} disabled={sendingReport} data-testid="intel-spend-report-button"
              className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/[0.04] px-3 py-2 font-mono text-[11px] uppercase text-gray-400 hover:text-white hover:border-[#00E5FF]/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Emails last week's spend vs budget summary now — also sent automatically every Monday">
              {sendingReport ? "Sending…" : "Email weekly report"}
            </button>
          </div>
        </div>
      </Panel>

      {/* Coverage */}
      <Panel title="Extraction coverage" icon={ShieldCheck} className="mt-6" testid="intel-coverage-panel"
        sub={cov ? `${cov.total_published} published in window · ${cov.sanitized} sanitized · articles enrich lazily on first view` : "…"}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ModuleCoverage label="Story Snapshot" c={cov?.snapshot} testid="cov-snapshot" />
          <ModuleCoverage label="Why This Matters" c={cov?.wtm} testid="cov-wtm" />
        </div>
      </Panel>

      {/* Policy operations (§41) */}
      {data?.policy_ops && (
        <Panel title="Policy operations" className="mt-6" testid="intel-policy-ops-panel"
          sub="Structured regulation intelligence health — records, link graph, status-history writer">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              ["Regulation records", data.policy_ops.regulation_records],
              ["Confirmed links", data.policy_ops.confirmed_links],
              ["Candidate backlog", data.policy_ops.candidate_backlog],
              ["History events", data.policy_ops.status_history?.events ?? 0],
            ].map(([label, v]: any) => (
              <div key={label}>
                <p className="font-mono text-xl font-bold text-white">{v}</p>
                <p className="font-mono text-[9px] uppercase tracking-wide text-gray-500 mt-1">{label}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-white/5 font-mono text-[10px] text-gray-500 space-y-1">
            <p>Status history: {data.policy_ops.status_history?.baselines ?? 0} baselines · {data.policy_ops.status_history?.transitions ?? 0} transitions · {data.policy_ops.status_history?.corrections ?? 0} corrections
              {data.policy_ops.status_history?.last_event_at && <> · last event {timeAgo(data.policy_ops.status_history.last_event_at)}</>}</p>
            {data.policy_ops.last_regulation_update && <p>Last structured record update {timeAgo(data.policy_ops.last_regulation_update)}</p>}
            {data.policy_ops.last_link_scan && <p>Last link rescan {timeAgo(data.policy_ops.last_link_scan)}</p>}
          </div>
        </Panel>
      )}

      {/* Needs attention */}
      <Panel title="Needs attention" icon={AlertTriangle} className="mt-6" testid="intel-attention-panel"
        sub="Failed extractions, stale retries and low-confidence routings">
        {(data?.needs_attention || []).length === 0 ? (
          <p className="font-mono text-xs text-gray-600 py-4">Nothing needs attention right now.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="font-mono text-[10px] uppercase tracking-widest text-gray-500 text-left">
                  <th className="pb-2 pr-4">Article</th><th className="pb-2 pr-4">State</th>
                  <th className="pb-2 pr-4">Issue</th><th className="pb-2 pr-4">Attempts</th>
                  <th className="pb-2 pr-4">Last attempt</th><th className="pb-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {data.needs_attention.map((n: any, i: number) => (
                  <tr key={`${n.id}-${i}`} className="font-mono text-xs" data-testid={`attention-row-${i}`}>
                    <td className="py-2.5 pr-4 text-white max-w-[260px] truncate">{n.title}</td>
                    <td className="py-2.5 pr-4"><StateBadge state={n.state} /></td>
                    <td className="py-2.5 pr-4 text-gray-400 max-w-[220px] truncate">{n.issue}</td>
                    <td className="py-2.5 pr-4 text-gray-500">{n.attempts ?? "—"}</td>
                    <td className="py-2.5 pr-4 text-gray-500 whitespace-nowrap">{n.last_attempt_at ? timeAgo(n.last_attempt_at) : "—"}</td>
                    <td className="py-2.5 text-right">
                      <button onClick={() => retry(n.id, n.state === "LOW_CONFIDENCE")}
                        disabled={paused || retrying === n.id} data-testid={`attention-retry-${i}`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-2 py-1 text-[10px] uppercase text-gray-300 hover:text-white hover:border-[#00E5FF]/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                        <RefreshCw className={`h-3 w-3 ${retrying === n.id ? "animate-spin" : ""}`} /> Retry
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {paused && (data?.needs_attention || []).length > 0 && (
          <p className="font-mono text-[10px] text-amber-400/80 mt-3">Manual retry is disabled while the extraction service is paused — jobs stay queued for automatic retry.</p>
        )}
      </Panel>

      {/* Article intelligence browser */}
      <Panel title="Article intelligence" icon={BrainCircuit} className="mt-6" testid="intel-articles-panel"
        sub={arts ? `${arts.total} of ${arts.scanned} latest published articles${filter ? ` · filtered ${filter.replace(/_/g, " ").toLowerCase()}` : ""}` : "…"}>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => { setFilter(f.key); setLimit(25); }} data-testid={`intel-filter-${f.key || "all"}`}
              className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors ${filter === f.key ? "border-[#00E5FF]/50 bg-[#00E5FF]/10 text-white" : "border-white/10 text-gray-500 hover:text-gray-300"}`}>
              {f.label}{arts?.counts && f.key ? ` ${arts.counts[f.key] ?? 0}` : ""}
            </button>
          ))}
          <div className="relative ml-auto">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-600" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { setQ(search); setLimit(25); } }}
              placeholder="Search title or slug ⏎" data-testid="intel-search-input"
              className="w-56 rounded-lg border border-white/10 bg-white/[0.03] pl-8 pr-3 py-1.5 font-mono text-[11px] text-white placeholder:text-gray-600 focus:outline-none focus:border-[#00E5FF]/40" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-widest text-gray-500 text-left">
                <th className="pb-2 pr-4">Article</th><th className="pb-2 pr-4">Type</th>
                <th className="pb-2 pr-4">State</th><th className="pb-2 pr-4">Snapshot</th>
                <th className="pb-2 pr-4">WTM</th><th className="pb-2 pr-4">Sanitized</th>
                <th className="pb-2 pr-4">Last job</th><th className="pb-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {(arts?.articles || []).map((a: any, i: number) => (
                <tr key={a.id} className="font-mono text-xs" data-testid={`intel-article-row-${i}`}>
                  <td className="py-2.5 pr-4 max-w-[280px]">
                    <a href={`/article/${a.slug}`} target="_blank" rel="noreferrer" className="text-white hover:text-[#00E5FF] transition-colors block truncate">{a.title}</a>
                  </td>
                  <td className="py-2.5 pr-4 text-gray-500">{a.content_type || "—"}</td>
                  <td className="py-2.5 pr-4"><StateBadge state={a.state} /></td>
                  <td className="py-2.5 pr-4">
                    {a.suppressed.snapshot ? <span className="text-gray-600">off</span>
                      : a.snapshot_facts > 0 ? <span className="text-green-400">{a.snapshot_facts} facts{a.locked.snapshot ? " 🔒" : ""}</span>
                        : <span className="text-gray-600">—</span>}
                  </td>
                  <td className="py-2.5 pr-4">
                    {a.suppressed.wtm ? <span className="text-gray-600">off</span>
                      : a.wtm ? <span className="text-green-400">✓{a.locked.wtm ? " 🔒" : ""}</span>
                        : <span className="text-gray-600">—</span>}
                  </td>
                  <td className="py-2.5 pr-4 text-gray-500">{a.sanitized_blocks > 0 ? `${a.sanitized_blocks} blocks` : "—"}</td>
                  <td className="py-2.5 pr-4 max-w-[180px]">
                    {a.job ? (
                      <span className={a.job.state === "FAILED" ? "text-red-400" : a.job.state === "PAUSED" ? "text-amber-400" : "text-gray-500"}>
                        {a.job.state}{a.job.error_category ? ` · ${a.job.error_category}` : ""}
                      </span>
                    ) : <span className="text-gray-600">—</span>}
                  </td>
                  <td className="py-2.5 text-right">
                    {a.state !== "NOT_ELIGIBLE" && a.state !== "EDITOR_LOCKED" && (
                      <button onClick={() => retry(a.id, a.state === "COMPLETE" || a.state === "PARTIAL")}
                        disabled={paused || retrying === a.id} data-testid={`article-retry-${i}`}
                        title={a.state === "COMPLETE" || a.state === "PARTIAL" ? "Force re-extraction" : "Retry extraction"}
                        className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-2 py-1 text-[10px] uppercase text-gray-300 hover:text-white hover:border-[#00E5FF]/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                        <RefreshCw className={`h-3 w-3 ${retrying === a.id ? "animate-spin" : ""}`} /> Retry
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(arts?.articles || []).length === 0 && (
            <p className="font-mono text-xs text-gray-600 py-6">No articles match this filter.</p>
          )}
        </div>
        {arts && arts.total > (arts.articles || []).length && limit < 100 && (
          <button onClick={() => setLimit((l) => Math.min(100, l + 25))} data-testid="intel-load-more"
            className="mt-4 w-full rounded-lg border border-white/10 py-2 font-mono text-[11px] uppercase text-gray-400 hover:text-white hover:border-[#00E5FF]/30 transition-colors">
            Load more ({arts.total - (arts.articles || []).length} remaining)
          </button>
        )}
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Sanitizer */}
        <Panel title="Sanitizer audit" icon={Eraser} testid="intel-sanitizer-panel"
          sub={san ? `${san.removed_blocks} promotional blocks removed across ${san.articles} imported articles (all-time)` : "…"}>
          {(san?.rules || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {san.rules.map((r: any) => (
                <span key={r.rule} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] ${r.anomaly ? "border-red-500/40 text-red-400" : "border-white/10 text-gray-400"}`}>
                  {r.anomaly && <AlertTriangle className="h-3 w-3" />}{r.rule} <span className="font-bold text-white">{r.count}</span>
                </span>
              ))}
            </div>
          )}
          {(san?.recent || []).length === 0 ? (
            <p className="font-mono text-xs text-gray-600 py-4">No removals logged yet — the sanitizer runs as imported articles are viewed.</p>
          ) : (
            <div className="space-y-2.5 max-h-72 overflow-y-auto pr-1">
              {san.recent.map((r: any, i: number) => (
                <div key={i} className="font-mono text-[11px]" data-testid={`sanitizer-row-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[#A78BFA] shrink-0">{r.rule}</span>
                    <span className="text-white truncate">{r.title}</span>
                    <span className="text-gray-600 ml-auto shrink-0 whitespace-nowrap">{r.at ? timeAgo(r.at) : ""}</span>
                  </div>
                  <p className="text-gray-500 truncate">{r.text}</p>
                </div>
              ))}
            </div>
          )}
        </Panel>

        {/* Editorial overrides */}
        <Panel title="Editorial overrides" icon={PenLine} testid="intel-overrides-panel"
          sub="Editor-locked and curated intelligence — never overwritten by automation">
          <div className="flex flex-wrap gap-1.5 mb-4">
            <Chip label="context" value={ov?.counts?.context_override ?? 0} tone="text-[#A78BFA]" />
            <Chip label="snapshot locks" value={ov?.counts?.snapshot_locked ?? 0} tone="text-[#A78BFA]" />
            <Chip label="WTM locks" value={ov?.counts?.wtm_locked ?? 0} tone="text-[#A78BFA]" />
            <Chip label="related pins" value={ov?.counts?.related_pins ?? 0} />
            <Chip label="go deeper" value={ov?.counts?.go_deeper ?? 0} />
            <Chip label="suppressed" value={ov?.counts?.suppressed ?? 0} />
          </div>
          {(ov?.recent || []).length === 0 ? (
            <p className="font-mono text-xs text-gray-600 py-4">No editorial overrides yet.</p>
          ) : (
            <div className="space-y-2.5 max-h-72 overflow-y-auto pr-1">
              {ov.recent.map((r: any, i: number) => (
                <div key={r.id} className="flex items-center gap-2 font-mono text-[11px]" data-testid={`override-row-${i}`}>
                  <Lock className="h-3 w-3 text-[#A78BFA] shrink-0" />
                  <span className="text-white truncate">{r.title}</span>
                  <span className="text-gray-500 ml-auto shrink-0">{r.locks.join(", ")}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* AI usage */}
      <div className="mt-6 bg-[#0d1117] border border-[#00E5FF]/20 rounded-xl p-5" data-testid="intel-usage-panel">
        <h3 className="flex items-center gap-2 font-heading font-bold text-white text-sm mb-3">
          <DollarSign className="h-4 w-4 text-[#22C55E]" /> AI usage by task (7d)
        </h3>
        <div className="flex flex-wrap gap-x-8 gap-y-2 font-mono text-xs">
          {(data?.usage?.d7.kinds || []).length === 0 ? (
            <span className="text-gray-600">No AI calls recorded in the last 7 days.</span>
          ) : data.usage.d7.kinds.map((k: any) => (
            <span key={k.kind} className="text-gray-400">
              {k.kind}: <span className="text-white font-bold">{k.count}</span>
              <span className="text-gray-600"> (~${k.cost})</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
